import { createContext, useContext, useRef, useState, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Wand2, X } from "lucide-react";

export type GenerateItem = {
  id: string;
  type: "recipe" | "banner" | "module";
  title: string;
  description?: string;
  category?: string;
};

type GenerationState = {
  running: boolean;
  done: number;
  failed: number;
  total: number;
  currentTitle: string;
};

type ImageGenerationContextType = {
  state: GenerationState;
  start: (items: GenerateItem[]) => void;
  cancel: () => void;
};

const INITIAL_STATE: GenerationState = {
  running: false,
  done: 0,
  failed: 0,
  total: 0,
  currentTitle: "",
};

const ImageGenerationContext = createContext<ImageGenerationContextType>({
  state: INITIAL_STATE,
  start: () => {},
  cancel: () => {},
});

// ── Floating status bar (visible on every page while running) ──────────────
function GenerationStatusBar({ state, cancel }: { state: GenerationState; cancel: () => void }) {
  if (!state.running && state.total === 0) return null;

  const pct = state.total > 0 ? Math.round(((state.done + state.failed) / state.total) * 100) : 0;
  const finished = !state.running && state.total > 0;

  return (
    /* A faixa era verde-esmeralda quando terminava. Verde no app tem UM
       significado (glicemia boa) e esta barra não fala de glicemia: as duas
       situações passam a usar o latão, e quem diz que acabou é o texto. A
       tinta sobre a faixa vem de --accent-foreground, que existe nos dois
       temas — "text-white" só funcionava por acidente. */
    <div className="fixed top-0 left-0 right-0 z-[200] transition-all duration-300 bg-accent">
      {/* Preenchimento do progresso */}
      <div
        className="h-0.5 transition-all duration-500"
        style={{ width: `${pct}%`, background: "hsl(var(--accent-foreground) / 0.4)" }}
      />
      <div className="max-w-[520px] mx-auto flex items-center gap-3 px-4 py-2">
        <Wand2 className="h-5 w-5 shrink-0 text-accent-foreground" strokeWidth={2.2} />
        <div className="flex-1 min-w-0">
          <p
            className="font-bold leading-tight truncate text-accent-foreground"
            style={{ fontSize: "var(--t-apoio)" }}
          >
            {finished
              ? `${state.done} imagens geradas${state.failed > 0 ? ` · ${state.failed} com erro` : ""}`
              : `Gerando imagens… ${state.done + state.failed}/${state.total}`}
          </p>
          {state.running && state.currentTitle && (
            <p
              className="truncate leading-tight text-accent-foreground/75"
              style={{ fontSize: "var(--t-etiqueta)" }}
            >
              {state.currentTitle}
            </p>
          )}
        </div>
        {state.running && (
          <button
            onClick={cancel}
            className="flex shrink-0 items-center justify-center text-accent-foreground transition-colors"
            style={{ width: "var(--alvo-min)", height: "var(--alvo-min)" }}
            title="Cancelar geração"
          >
            <X className="h-6 w-6" strokeWidth={2.5} />
          </button>
        )}
        {finished && (
          <button
            onClick={cancel} // resets state
            className="flex shrink-0 items-center justify-center text-accent-foreground transition-colors"
            style={{ width: "var(--alvo-min)", height: "var(--alvo-min)" }}
          >
            <X className="h-6 w-6" strokeWidth={2.5} />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Provider ───────────────────────────────────────────────────────────────
export function ImageGenerationProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GenerationState>(INITIAL_STATE);
  const abortRef = useRef(false);
  const runningRef = useRef(false);

  const cancel = useCallback(() => {
    abortRef.current = true;
    runningRef.current = false;
    setState(INITIAL_STATE);
  }, []);

  const start = useCallback(async (items: GenerateItem[]) => {
    if (!items.length) {
      toast({ title: "Sem imagens para gerar", description: "Todos já têm imagem." });
      return;
    }
    if (runningRef.current) {
      toast({ title: "Geração já em andamento", description: "Aguarde terminar ou cancele." });
      return;
    }

    abortRef.current = false;
    runningRef.current = true;
    setState({ running: true, done: 0, failed: 0, total: items.length, currentTitle: items[0]?.title ?? "" });

    let done = 0;
    let failed = 0;

    for (let i = 0; i < items.length; i++) {
      if (abortRef.current) break;

      const item = items[i];

      setState(prev => ({ ...prev, currentTitle: item.title, done, failed }));

      try {
        const { data, error } = await supabase.functions.invoke("generate-us-images", {
          body: { items: [item] },
        });

        if (error) {
          failed++;
          console.error("[ImageGen] SDK error:", error);
          toast({ title: "Erro na edge function", description: String(error.message ?? error), variant: "destructive" });
          break;
        } else {
          const results: any[] = data?.results || [];
          const result = results[0];
          if (result?.success) {
            done++;
          } else {
            failed++;
            const errMsg: string = result?.error ?? "(sem mensagem)";
            console.error("[ImageGen] API error:", errMsg);

            // Always show the error so we can diagnose
            toast({ title: `Falhou: ${item.title}`, description: errMsg.slice(0, 300), variant: "destructive", duration: 30000 });

            if (errMsg.startsWith("CREDITS:") || errMsg.startsWith("RATE_LIMIT:")) {
              break;
            }
          }
        }
      } catch (e: any) {
        failed++;
        console.error("[ImageGen] item error:", e);
      }

      setState(prev => ({ ...prev, done, failed }));
    }

    runningRef.current = false;
    setState(prev => ({ ...prev, running: false, done, failed, currentTitle: "" }));

    if (done > 0 && !abortRef.current) {
      toast({ title: `${done} imagens geradas com sucesso!` });
    }
  }, []);

  return (
    <ImageGenerationContext.Provider value={{ state, start, cancel }}>
      <GenerationStatusBar state={state} cancel={cancel} />
      {children}
    </ImageGenerationContext.Provider>
  );
}

export const useImageGeneration = () => useContext(ImageGenerationContext);
